import { z } from 'zod';

/**
 * Account deletion, `DELETE /api/account` (F6 AC-2.4, T-2.5; Luật 91/2025/QH15: quyền xoá dữ liệu).
 *
 * The user confirms by typing their own email address (step 2 of the app's confirmation): unlike a
 * fixed word, an address needs no Vietnamese keyboard and cannot be typed by reflex. The server
 * compares it, trimmed and case-insensitively, with the signed-in user's address; a mismatch is a 422
 * `confirmation_mismatch`, a missing or empty field a 422 `validation_failed`.
 */
export const AccountDeleteInputSchema = z.strictObject({
  confirmEmail: z.string().trim().min(1).max(320),
});
export type AccountDeleteInput = z.infer<typeof AccountDeleteInputSchema>;

/** 200 answer: everything of the account is gone and the session cookie is cleared. */
export const AccountDeletedSchema = z.strictObject({ deleted: z.literal(true) });
export type AccountDeleted = z.infer<typeof AccountDeletedSchema>;

/** Error codes of `DELETE /api/account` besides the standard ones (401, 403 `banned`, 422). */
export const ACCOUNT_DELETE_ERRORS = {
  /** The typed address is not the account's (422). */
  confirmationMismatch: 'confirmation_mismatch',
  /** The session is older than the re-authentication window: sign in again first (403). */
  reauthRequired: 'reauth_required',
  /** The account is the last active admin (409): promote someone else first. */
  lastAdmin: 'last_admin',
} as const;

/** How recent the sign-in must be to delete the account (Better Auth's own `freshAge` default). */
export const ACCOUNT_DELETE_FRESH_SESSION_MS = 24 * 60 * 60 * 1000;
